import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';
import { ExperienceTracker } from '@gonfalon/analytics/ExperienceTracker';

export const trackSdkSetupEvent = createTrackerForCategory('SDK Setup');

export type SdkInstallInitiatedMethod = 'aiPrompt' | 'agentDeepLink' | 'cli' | 'cursor' | 'draftPr' | 'snippet';

export const trackSdkInstallInitiated = (method: SdkInstallInitiatedMethod) =>
  trackSdkSetupEvent('SDK Install Initiated', { method });

export const trackCursorSdkInstallButtonClicked = () => {
  trackSdkSetupEvent('Cursor SDK Install Button Clicked');
  trackSdkInstallInitiated('cursor');
};

export const trackSdkSetupGetSupportClicked = () => trackSdkSetupEvent('Get Support Clicked');

export const trackSdkSetupExploreSandboxClicked = () => trackSdkSetupEvent('Explore Sandbox Clicked');

/**
 * Where `Close setup` sent the member. `back` pops history instead of navigating to a page, so it
 * has no fixed destination. Reported because a member with no project access is routed away from
 * the flags list, and the split is only visible here.
 */
export type SdkSetupCloseDestination = 'back' | 'flags' | 'personalSettings' | 'root';

export const trackSdkSetupCloseClicked = (destination: SdkSetupCloseDestination) =>
  trackSdkSetupEvent('Close Clicked', { destination });

export const trackSdkSetupSdkConnected = (sdk: string) => trackSdkSetupEvent('SDK Connected', { sdk });

export const trackSdkSetupNextStepsCTAClicked = () => trackSdkSetupEvent('Next Steps CTA Clicked');

export const trackSdkSetupSdkSelected = (sdk: string) => trackSdkSetupEvent('SDK Selected', { sdk });

export const trackSdkSetupFilterSelected = (filter: string) => trackSdkSetupEvent('Filter Selected', { filter });

export const trackSdkSetupReadDocsClicked = (sdk: string) => trackSdkSetupEvent('Read Docs Clicked', { sdk });

export const trackSdkSetupSnippetClicked = (snippet: string, environmentKey?: string) =>
  trackSdkSetupEvent('Snippet Clicked', { snippet, environmentKey });

export const trackSdkSetupInstallSnippetClicked = (snippet: string, environmentKey?: string) => {
  trackSdkSetupSnippetClicked(snippet, environmentKey);
  trackSdkInstallInitiated('snippet');
};

export const trackSdkSetupEnvironmentSelected = (environmentKey: string) =>
  trackSdkSetupEvent('Environment Selected', { environmentKey });

export const trackSdkSetupKeyModeToggled = (keyMode: 'literal' | 'envVar') =>
  trackSdkSetupEvent('Key Mode Toggled', { keyMode });

export const trackSdkSetupPageLoaded = (method: string) => trackSdkSetupEvent('SDK Setup Page Loaded', { method });

export const trackInstallSdkClicked = ({ cta, component }: { cta: string; component: string }) =>
  trackSdkSetupEvent('Install SDK Clicked', { cta, component });

export const trackProjectSettingsLinkClicked = (sdk: string) =>
  trackSdkSetupEvent('Project Settings Link Clicked', { sdk });

export const trackSdkKeysLinkClicked = (sdk: string) => trackSdkSetupEvent('SDK Keys Link Clicked', { sdk });

export const trackAgentCommandCopied = () => {
  trackSdkSetupEvent('Agentic Onboarding Command Copied');
  trackSdkInstallInitiated('aiPrompt');
};

export const trackCliSetupCommandCopied = () => {
  trackSdkSetupEvent('CLI Setup Command Copied');
  trackSdkInstallInitiated('cli');
};

export const trackMcpSetupSnippetCopied = (client: string) =>
  trackSdkSetupEvent('MCP Setup Snippet Copied', { client });

export const trackAgentDeepLinkClicked = (agent: string) => {
  trackSdkSetupEvent('Agent Deep Link Clicked', { agent });
  trackSdkInstallInitiated('agentDeepLink');
};

/** Install method the account was served: an experiment arm, or the manual path. */
export type InstallMethod = 'agent' | 'cli' | 'manual';

/** The two arms of the install-method experiment; `manual` is not an arm. */
export type InstallArm = Exclude<InstallMethod, 'manual'>;

// One event name across both arms, so a single conversion metric can compare them. The
// per-arm events above stay for continuity with existing dashboards.
export const trackInstallCommandCopied = (installMethod: InstallArm) =>
  trackSdkSetupEvent('Install Command Copied', { installMethod });

// Primary outcome for the CLI-vs-agent experiment: the SDK is sending events, whichever path
// got the account there. Reports on every call — prefer trackSdkSetupCompletedOnce below, which
// is what keeps a single completion from being reported by more than one surface.
export const trackSdkSetupCompleted = ({ sdk, installMethod }: { sdk?: string; installMethod: InstallMethod }) =>
  trackSdkSetupEvent('SDK Setup Completed', { sdk, installMethod });

let hasTrackedSetupCompletion = false;

/**
 * Report setup completion at most once per page load. The surfaces that detect it live in
 * method panels that unmount when another method is selected, so a per-component guard
 * refires on remount — and would credit an already-connected manual setup to whichever
 * install arm the account opens next.
 */
export const trackSdkSetupCompletedOnce = (args: { sdk?: string; installMethod: InstallMethod }) => {
  if (hasTrackedSetupCompletion) {
    return;
  }

  hasTrackedSetupCompletion = true;
  trackSdkSetupCompleted(args);
};

/** Test seam: clears the once-per-page-load guard above. */
export const resetSdkSetupCompletionTracking = () => {
  hasTrackedSetupCompletion = false;
};

export const trackSdkSetupResourceLinkClicked = (linkName: string) =>
  trackSdkSetupEvent('Resource Link Clicked', { linkName });

export const trackSdkSetupMethodSelected = (method: string, fromMethod: string) =>
  trackSdkSetupEvent('Method Selected', { method, fromMethod });

export const trackSdkSetupStepExpanded = (step: string) => trackSdkSetupEvent('Step Expanded', { step });

export const trackSdkSetupStepAdvanced = (from: string, to: string) =>
  trackSdkSetupEvent('Step Advanced', { from, to });

// GitHub draft-PR install path (REL-15719). Connect is not a step here: the shared connect
// affordance already emits `trackGitHubConnectClicked` with `component: 'GithubSetupPage'`.
export type SdkSetupDraftPrEvent =
  | 'Advanced To Draft PR Step'
  | 'Returned To Repo Step'
  | 'Draft PR Created'
  | 'Draft PR Merged'
  | 'Review Clicked'
  | { eventName: 'Draft PR Requested'; attributes: { repositories: number } };

export const sdkSetupDraftPrTracker = ExperienceTracker.create<SdkSetupDraftPrEvent>('SDK Setup Draft PR', {
  category: 'SDK Setup',
  owners: ['team-foundation'],
});

// A bfcache restore does not remount, so the funnel has to be resumed by hand — but only one that
// was still running. Resuming after a success opens a second funnel that nothing can finish,
// because the guard on reporting a connected SDK survives the freeze too, and the eventual unmount
// would report that funnel as abandoned.
let endedInSuccess = false;

/** Started on render, not on the method card: reloads and resumes belong in the denominator. */
export const trackSdkSetupDraftPrPathStarted = () => {
  endedInSuccess = false;
  sdkSetupDraftPrTracker.start();
};

/** For a page coming back from the bfcache. See `endedInSuccess`. */
export const trackSdkSetupDraftPrPathRestored = () => {
  if (endedInSuccess) {
    return;
  }
  sdkSetupDraftPrTracker.start();
};

export const trackSdkSetupDraftPrStepAdvanced = () => sdkSetupDraftPrTracker.track('Advanced To Draft PR Step');

/** The repo step's header is the only way back; there is no Back button. */
export const trackSdkSetupDraftPrReturnedToRepoStep = () => sdkSetupDraftPrTracker.track('Returned To Repo Step');

export const trackSdkSetupDraftPrRequested = (repositories: number) => {
  sdkSetupDraftPrTracker.track('Draft PR Requested', { repositories });
  trackSdkInstallInitiated('draftPr');
};

export const trackSdkSetupDraftPrCreated = () => sdkSetupDraftPrTracker.track('Draft PR Created');

export const trackSdkSetupDraftPrMerged = () => sdkSetupDraftPrTracker.track('Draft PR Merged');

export const trackSdkSetupDraftPrReviewClicked = () => sdkSetupDraftPrTracker.track('Review Clicked');

export const trackSdkSetupDraftPrPathSucceeded = () => {
  endedInSuccess = true;
  sdkSetupDraftPrTracker.end({ outcome: 'success' });
};

/** `end` no-ops once ended, so leaving after a success does not overwrite it. */
export const trackSdkSetupDraftPrPathAbandoned = () => sdkSetupDraftPrTracker.end({ outcome: 'cancel' });

export const trackSdkSetupDraftPrGenAiRequiredViewed = () => trackSdkSetupEvent('Draft PR GenAI Required Viewed');

export const trackSdkSetupDraftPrGenAiSettingsClicked = () => trackSdkSetupEvent('Draft PR GenAI Settings Clicked');

// Sample-app-offer funnel (REL-15115): the same category is used on the welcome-survey surface
// (packages/onboarding) so shown → clicked reads as one funnel across both entry points. The
// offer renders on two SDK-setup surfaces (the page and the drawer), so `component` is threaded
// from the call site to name the surface rather than hardcoded. Gated by sample-app-offer.
const trackSampleAppOfferEvent = createTrackerForCategory('Sample App Offer');
export const trackSampleAppOfferShown = (component: string) =>
  trackSampleAppOfferEvent('Sample App Offer Shown', { component });
export const trackSampleAppOfferClicked = (component: string) =>
  trackSampleAppOfferEvent('Sample App Offer Clicked', { component });

// Event name is the key for the sdk-setup-agent-control-link-clicked metric; renaming detaches it.
export const trackAgentControlSetupLinkClicked = () => trackSdkSetupEvent('Agent Control Setup Link Clicked');

// Add flag to code events. v3 (release-add-flag-to-code-v3) is an A/B test of the Code tab plus an
// inline targeting CTA against a control that shows nothing, so the surviving entry points are the
// Code tab and the inline targeting button.
export const trackAddToCodeEvent = createTrackerForCategory('Add Flag To Code');
export const trackAddToCodeTabClicked = () => trackAddToCodeEvent('Add Flag To Code Tab Clicked');
export const trackAddToCodeTargetingButtonClicked = () =>
  trackAddToCodeEvent('Add Flag To Code Targeting Button Clicked');
export const trackAddToCodeSnippetCopied = ({ sdk, flagType }: { sdk: string; flagType: string }) =>
  trackAddToCodeEvent('Add Flag To Code Snippet Copied', { sdk, flagType });

// SDK misconfiguration surfacing (epic REL-15674). Measures how often each
// misconfiguration is detected on the connect page and whether routing users to
// Live Events recovers them. Modeled as an Experience so the standard funnel
// tooling (category, owner attribution, session/duration correlation) applies.
// The events are tracked directly rather than wrapped in a start/end lifecycle:
// the recovery outcome is observed by a different surface than the one that
// mounts the banner (the banner unmounts the moment the SDK connects), so an
// accurate success/cancel `end` would need host-level lifecycle wiring that is
// out of scope here. Event name + reason vocabulary should be confirmed against
// REL-14070 / the Foundation Mixpanel conventions before launch.
// PII: project + SDK name + environment keys only — never flag/context/credential values.
export type SdkMisconfigReason = 'wrong_environment' | 'not_detected_2min';

type SdkMisconfigurationEvent =
  | {
      eventName: 'Detected';
      attributes: {
        reason: SdkMisconfigReason;
        projectKey: string;
        sdkName?: string;
        environmentKey?: string;
        /** Only set for `wrong_environment`: the env the events actually landed in. */
        detectedEnvironmentKey?: string;
      };
    }
  | { eventName: 'Opened Live Events'; attributes: { reason: SdkMisconfigReason; environmentKey?: string } };

export const sdkMisconfigurationTracker = ExperienceTracker.create<SdkMisconfigurationEvent>(
  'SDK Setup Misconfiguration',
  { category: 'SDK Setup', owners: ['team-foundation'] },
);

export const trackSdkMisconfigurationDetected = (params: {
  reason: SdkMisconfigReason;
  projectKey: string;
  sdkName?: string;
  environmentKey?: string;
  /** Only set for `wrong_environment`: the env the events actually landed in. */
  detectedEnvironmentKey?: string;
}) => sdkMisconfigurationTracker.track('Detected', params);

// Detection can be re-evaluated across polls/remounts and multiple hosts render
// the shared diagnosis, so dedupe to one event per (project, reason, environment)
// per page load — mirroring trackSdkSetupCompletedOnce. The project is part of the
// key because environment keys like `production` recur across projects and the
// guard survives SPA project switches on the same page load.
const trackedMisconfigurations = new Set<string>();

export const trackSdkMisconfigurationDetectedOnce = (params: {
  reason: SdkMisconfigReason;
  projectKey: string;
  sdkName?: string;
  environmentKey?: string;
  detectedEnvironmentKey?: string;
}) => {
  const key = `${params.projectKey}:${params.reason}:${params.environmentKey ?? ''}`;
  if (trackedMisconfigurations.has(key)) {
    return;
  }
  trackedMisconfigurations.add(key);
  trackSdkMisconfigurationDetected(params);
};

/** Test seam: clears the once-per-page-load misconfiguration guard above. */
export const resetSdkMisconfigurationTracking = () => {
  trackedMisconfigurations.clear();
};

/** Fired when the user follows a misconfiguration state into Live Events. */
export const trackSdkMisconfigurationLiveEventsClicked = (params: {
  reason: SdkMisconfigReason;
  environmentKey?: string;
}) => sdkMisconfigurationTracker.track('Opened Live Events', params);
